import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import Webcam from "react-webcam";
import { Camera, UploadSimple, ArrowCounterClockwise, Check, X, Aperture, ArrowsClockwise } from "@phosphor-icons/react";

const HEIC_RE = /(heic|heif)/i;

async function getCroppedBlob(imageSrc, area) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.crossOrigin = "anonymous";
    img.src = imageSrc;
  });
  const size = 512;
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.9));
}

export default function PlayerPhotoEditor({ onSave, onClose, busy }) {
  const [mode, setMode] = useState("choose"); // choose | camera | crop
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [camError, setCamError] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const webcamRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        const cams = devices.filter((d) => d.kind === "videoinput");
        setHasMultipleCameras(cams.length > 1);
      })
      .catch(() => setHasMultipleCameras(false));
  }, []);

  const flipCamera = () => setFacingMode((m) => (m === "user" ? "environment" : "user"));
  const isFront = facingMode === "user";

  const onCropComplete = useCallback((_, px) => setArea(px), []);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (HEIC_RE.test(file.type) || HEIC_RE.test(file.name)) {
      // HEIC/HEIF can't be cropped in-browser — upload the raw file, server converts it.
      onSave(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setImageSrc(reader.result); setZoom(1); setCrop({ x: 0, y: 0 }); setMode("crop"); };
    reader.readAsDataURL(file);
  };

  const capture = () => {
    const shot = webcamRef.current?.getScreenshot();
    if (!shot) { setCamError(true); return; }
    setImageSrc(shot); setZoom(1); setCrop({ x: 0, y: 0 }); setMode("crop");
  };

  const saveCrop = async () => {
    if (!area) return;
    const blob = await getCroppedBlob(imageSrc, area);
    if (blob) onSave(new File([blob], "photo.jpg", { type: "image/jpeg" }));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" onClick={onClose} data-testid="photo-editor">
      <div className="bg-[#121212] border border-white/10 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-display text-xl uppercase flex items-center gap-2"><Camera size={20} weight="fill" className="text-[#FF5722]" /> Profile Photo</h3>
          <button onClick={onClose} data-testid="photo-editor-close" className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {mode === "choose" && (
            <div className="space-y-3" data-testid="photo-choose">
              <button onClick={() => { setCamError(false); setMode("camera"); }} data-testid="photo-take-btn" className="w-full flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide py-3 transition-colors"><Camera size={18} weight="bold" /> Take Photo</button>
              <button onClick={() => fileRef.current?.click()} data-testid="photo-upload-btn" className="w-full flex items-center justify-center gap-2 border border-white/20 hover:border-[#FF5722] hover:text-[#FF5722] text-white/80 font-bold uppercase tracking-wide py-3 transition-colors"><UploadSimple size={18} weight="bold" /> Upload Photo</button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.heic,.heif" onChange={onPickFile} className="hidden" data-testid="photo-file-input" />
              <p className="text-xs text-white/40 text-center">JPG, PNG, WEBP or HEIC · auto-cropped to a square and optimized.</p>
            </div>
          )}

          {mode === "camera" && (
            <div className="space-y-3" data-testid="photo-camera">
              {camError ? (
                <div className="text-center py-8 border border-dashed border-white/15">
                  <Aperture size={34} className="text-white/30 mx-auto mb-2" />
                  <p className="text-white/60 text-sm">Camera unavailable or permission denied.</p>
                  <button onClick={() => fileRef.current?.click()} className="mt-3 text-xs uppercase font-bold text-[#FF5722]">Upload instead</button>
                </div>
              ) : (
                <div className="relative bg-black aspect-square overflow-hidden">
                  <Webcam ref={webcamRef} audio={false} screenshotFormat="image/jpeg" mirrored={isFront}
                    videoConstraints={{ facingMode }} onUserMediaError={() => setCamError(true)}
                    className="w-full h-full object-cover" />
                  {hasMultipleCameras && (
                    <button onClick={flipCamera} data-testid="photo-flip-camera-btn" title="Flip camera"
                      className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/60 hover:bg-[#FF5722] backdrop-blur-sm text-white hover:text-black px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition-colors">
                      <ArrowsClockwise size={16} weight="bold" /> {isFront ? "Front" : "Rear"}
                    </button>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setMode("choose")} className="flex items-center justify-center gap-2 border border-white/20 text-white/70 hover:border-white font-bold uppercase tracking-wide text-sm px-4 py-2.5 transition-colors"><ArrowCounterClockwise size={16} /> Back</button>
                <button onClick={capture} disabled={camError} data-testid="photo-capture-btn" className="flex-1 flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 text-black font-bold uppercase tracking-wide text-sm py-2.5 transition-colors"><Aperture size={18} weight="bold" /> Capture</button>
              </div>
            </div>
          )}

          {mode === "crop" && (
            <div className="space-y-3" data-testid="photo-crop">
              <div className="relative w-full aspect-square bg-black overflow-hidden">
                <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1} cropShape="rect" showGrid
                  onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-wide text-white/50">Zoom</span>
                <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="photo-zoom" className="flex-1 accent-[#FF5722]" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setMode("choose")} data-testid="photo-retake-btn" className="flex items-center justify-center gap-2 border border-white/20 text-white/70 hover:border-white font-bold uppercase tracking-wide text-sm px-4 py-2.5 transition-colors"><ArrowCounterClockwise size={16} /> Retake</button>
                <button onClick={saveCrop} disabled={busy} data-testid="photo-save-btn" className="flex-1 flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide text-sm py-2.5 transition-colors"><Check size={18} weight="bold" /> {busy ? "Saving…" : "Save Photo"}</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
